/**
 * Users, per-home membership, access schedules, grants and invites.
 *
 * Resolution order (server/src/access/resolve.ts):
 *   1. No membership for the home  → no access.
 *   2. Membership outside its schedule → everything downgraded to `view` (read-only).
 *   3. Role baseline (owner/admin = everything; member/guest = grants only; custom role = its grants).
 *   4. Grants: most specific target wins (entity > device > room > floor > domain > home);
 *      at equal specificity deny beats allow. A room grant on a parent room (bedroom) also covers
 *      its child rooms (ensuite), ranked just below a grant on the child; a child grant covers only the child.
 */
import { z } from 'zod';
import { EntityId, HomeId, ModuleId } from './model.js';
/** Ordered: each implies the ones before it except `history`, which is independent. */
export const Capability = z.enum(['view', 'control', 'configure', 'history']);
export const Role = z.enum(['admin', 'member', 'guest', 'custom']);
export const Privilege = z.enum([
    'alarm_view', 'alarm_arm', 'alarm_disarm',
    'door_unlock', 'intercom_open',
    'wifi', 'house_info',
    'schedule_room', 'schedule_house',
    'boost',
]);
export const GrantTarget = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('home') }),
    z.object({ kind: z.literal('floor'), id: z.string() }),
    z.object({ kind: z.literal('room'), id: z.string() }),
    z.object({ kind: z.literal('device'), id: z.string() }),
    z.object({ kind: z.literal('entity'), id: EntityId }),
    z.object({ kind: z.literal('domain'), id: z.string() }),
    z.object({ kind: z.literal('module'), id: ModuleId }),
]);
export const Grant = z.object({
    id: z.string(),
    target: GrantTarget,
    capabilities: z.array(Capability),
    effect: z.enum(['allow', 'deny']),
});
/** Minutes since local midnight Monday (0..10079) in the home's timezone. Windows may wrap the week. */
export const WeeklyWindow = z.object({ start: z.number().int().min(0).max(10079), end: z.number().int().min(0).max(10080) });
export const ScheduleException = z.object({
    /** ISO local datetimes in the home's timezone. */
    from: z.string(),
    to: z.string(),
    effect: z.enum(['add', 'remove']),
    note: z.string().optional(),
});
/** null schedule = always in window. */
export const AccessSchedule = z.object({
    windows: z.array(WeeklyWindow),
    validFrom: z.string().nullable(),
    validUntil: z.string().nullable(),
    exceptions: z.array(ScheduleException),
});
export const Membership = z.object({
    homeId: HomeId,
    role: Role,
    customRoleId: z.string().nullable(),
    schedule: AccessSchedule.nullable(),
    grants: z.array(Grant),
    privileges: z.array(Privilege),
});
export const User = z.object({
    id: z.string(),
    username: z.string().min(2).max(40).regex(/^[a-z0-9._-]+$/),
    displayName: z.string(),
    isOwner: z.boolean(),
    disabled: z.boolean(),
    mustChangePassword: z.boolean(),
    /** Set for invite-code guests (no password). */
    inviteId: z.string().nullable(),
    createdAt: z.number(),
    lastSeenAt: z.number().nullable(),
    memberships: z.array(Membership),
});
/** Effective, resolved access for one user in one home, sent to the client. */
export const ResolvedAccess = z.object({
    homeId: HomeId,
    role: Role,
    isAdmin: z.boolean(),
    /** false when outside the access schedule → read-only. */
    inWindow: z.boolean(),
    /** Epoch seconds of the next window boundary, if any. */
    nextChangeAt: z.number().nullable(),
    /** entity_id → capabilities. Entities absent here are invisible to the user. */
    entities: z.record(z.string(), z.array(Capability)),
    modules: z.array(ModuleId),
    /** Modules the user may operate (module `control`, in window), e.g. music playback. Absent = none. */
    moduleControl: z.array(ModuleId).optional(),
    privileges: z.array(Privilege),
});
export const InviteStatus = z.enum(['scheduled', 'active', 'expired', 'revoked']);
/** An invite creates (on redeem) a passwordless guest user with these memberships. */
export const Invite = z.object({
    id: z.string(),
    code: z.string(),
    guestName: z.string(),
    note: z.string(),
    startsAt: z.number(),
    endsAt: z.number(),
    memberships: z.array(Membership),
    revokedAt: z.number().nullable(),
    createdAt: z.number(),
    lastSeenAt: z.number().nullable(),
    status: InviteStatus,
    sessionCount: z.number(),
    /** The passwordless guest user created on first redeem (null until then). */
    guestUserId: z.string().nullable().optional(),
    /** The guest stay this invite belongs to (modules/stays), if any. */
    stayId: z.string().nullable().optional(),
});
export const AuditEntry = z.object({
    id: z.number(),
    at: z.number(),
    userId: z.string().nullable(),
    username: z.string().nullable(),
    homeId: z.string().nullable(),
    kind: z.string(),
    entityId: z.string().nullable(),
    detail: z.string().nullable(),
    allowed: z.boolean(),
});
export const SessionInfo = z.object({
    id: z.string(),
    userId: z.string(),
    createdAt: z.number(),
    lastSeenAt: z.number(),
    userAgent: z.string().nullable(),
    current: z.boolean(),
});
const ORDER = ['view', 'control', 'configure'];
/** True if `have` satisfies `need` (control implies view, configure implies control). */
export function hasCapability(have, need) {
    if (!have)
        return false;
    if (need === 'history')
        return have.includes('history');
    const n = ORDER.indexOf(need);
    return have.some((c) => c !== 'history' && ORDER.indexOf(c) >= n);
}
//# sourceMappingURL=access.js.map